import { h, Component, createRef } from 'preact'
// @ts-expect-error
import Downshift from 'downshift/preact'
import Fetch from './Fetch'
import PillContainer from '../pills/PillContainer'
import Pill from '../pills/Pill'
import ItemList from './ItemList'
import Icon from '../assets'
import { Item } from './Item'

interface Config<T=any> {
  dynamic?: { // Whether the selector is capable of fetching data dynamically
    baseUrl: string
    omitItemsKey: string // The object key for the items to omit when fetching
    dynamicItemElement: (item: T) => T // The element that gets displayed in the choice dropdown
    responseKey?: string // The key that hold the array of choices
    imageKey?: string // The key that hold image information
  }
  itemElement: (item: T) => T // The element that gets displayed when an item is selected
  itemToStringKey: string // The key of the item which will get displayed when selected
  delimiter: string // When pasting a list, this is the character which will split the items
  keys: {
    insert: string[] // An array of key values which will trigger the insertion of the values (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values)
    remove: string[] // An array of key values which will trigger the deletion of the values
  }
}

interface Props<T=any> {
  onChange: (items: T[]) => void
  config: Config
  initialItems?: T[]
  placeholder?: string
  onValidate?: (value: string) => Promise<T>
  error: boolean
}

interface State {
  selectedItems: Item[]
}

class Selector extends Component<Props, State> {
  input = createRef()
  constructor (props: Props) {
    super()
    this.state = {
      selectedItems: props.initialItems?.map(initialItem => {
        initialItem.isValid = true
        return initialItem
      }) || []
    }
  }

  handleChange = (selectedItem: Item, downshift: Downshift) => {
    const item = Object.assign(selectedItem, { isValid: true })
    this.addItem(item, downshift.reset)
  }

  addItem = (item: Item, reset: Function) => {
    this.setState(
      ({ selectedItems }) => ({
        selectedItems: [...selectedItems, item]
      }),
      () => {
        reset()
        const selectedItems = this.state.selectedItems.filter(item => item.isValid)
        this.props.onChange(selectedItems)
      }
    )
  }

  onRemoveItem = (item: Item) => {
    this.setState(
      ({ selectedItems }) => ({
        selectedItems: selectedItems.filter(c => c !== item)
      }),
      () => {
        this.input.current.focus()
        const selectedItems = this.state.selectedItems.filter(item => item.isValid)
        this.props.onChange(selectedItems)
      }
    )
  }

  validateItem = async (value: string, reset: Function) => {
    const { onValidate } = this.props
    const { itemToStringKey } = this.props.config

    if (onValidate) {
      let item = await onValidate(value)
      if (item) {
        item = Object.assign(item, { isValid: true })
        this.addItem(item, reset)
      } else {
        const item = { [itemToStringKey]: value, isValid: false }
        this.addItem(item, reset)
      }
    } else {
      const item = { [itemToStringKey]: value, isValid: true }
      this.addItem(item, reset)
    }
  }

  onPaste = ({
    event,
    reset,
    omitItems
  }: {
    event: ClipboardEvent
    reset: Function
    omitItems: string[]
  }) => {
    const { delimiter } = this.props.config
    const { clipboardData } = event

    if (!clipboardData) return

    const value = clipboardData.getData('Text')
    const isDelimeterSeparatedList = value.search(delimiter) !== -1

    if (isDelimeterSeparatedList) {
      let values = value
        .split(delimiter)
        .filter((x: string) => x.trim())
        .map((y: string) => y.trim())

      values = [...new Set(values)] // Remove duplicate values in array
      values = values.filter((item: string) => !omitItems.includes(item)) // Filter out selected already selected items

      values.forEach((value: string) => {
        this.validateItem(value, reset)
      })
    }
  }

  onKeyDown = ({
    event,
    selectHighlightedItem,
    highlightedIndex,
    isOpen,
    reset
  }: {
    event: KeyboardEvent
    selectHighlightedItem: Function
    highlightedIndex: number
    isOpen: boolean
    reset: Function
  }) => {
    const { selectedItems } = this.state
    const { itemToStringKey } = this.props.config
    const { insert, remove } = this.props.config.keys
    const { value } = event.target as HTMLInputElement
    const { key } = event

    if (remove.includes(key)) {
      if (value) return
      const item = selectedItems[selectedItems.length - 1]

      this.onRemoveItem(item)
      return
    }

    if (insert.includes(key)) {
      if (highlightedIndex === null) {
        if (!value) return
        const selectedItems = this.state.selectedItems.map((item: Item) => item[itemToStringKey])
        if (selectedItems.includes(value)) return

        this.validateItem(value, reset)
        return
      }

      if (isOpen) {
        event.preventDefault()
        selectHighlightedItem()
      }
    }
  }

  itemToString = (item: Item) => {
    const { itemToStringKey } = this.props.config
    return item ? item[itemToStringKey] : ''
  }

  private filterItems (items: Item[], selectedItems: Item[], omitItemsKey: string) {
    const omitItems = selectedItems.map(item => item[omitItemsKey])
    return items
      .filter((item: Item) => !omitItems.includes(item[omitItemsKey]))
      .slice(0, 10)
  }

  render (props: Props, state: State) {
    const dynamicConfig = props.config.dynamic || null
    const { itemToStringKey, itemElement } = props.config
    const { selectedItems } = state

    return (
      <Downshift
        selectedItem={null}
        onChange={this.handleChange}
        itemToString={this.itemToString}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          highlightedIndex,
          selectHighlightedItem,
          setHighlightedIndex,
          reset,
          inputValue,
          clearItems,
          setItemCount
        }: Downshift) => {
          return (
            <div class={`selector ${props.error ? 'invalid' : ''}`}>
              <PillContainer>
                {selectedItems
                  .map((item, idx) => {
                    return (
                      <Pill key={idx} isValid={this.props.onValidate ? item.isValid : true}>
                        {itemElement(item)}
                        <Icon customClass='qa-dismiss-filter-pill' icon='close' width={16} height={16} onClick={() => this.onRemoveItem(item)} />
                      </Pill>
                    )
                  }
                  )}
                <div class='selector__input-wrapper'>
                  <input {...getInputProps({
                    ref: this.input,
                    placeholder: this.props.placeholder,
                    onPaste: (event: ClipboardEvent) => this.onPaste({
                      event,
                      reset,
                      omitItems: selectedItems.map(item => item[itemToStringKey])
                    }),
                    onKeyDown: (event: KeyboardEvent) => this.onKeyDown({
                      event,
                      selectHighlightedItem,
                      highlightedIndex,
                      isOpen,
                      reset
                    })
                  })}
                  />
                  {
                    dynamicConfig && inputValue
                      ? isOpen &&
                        <Fetch
                          baseUrl={dynamicConfig.baseUrl}
                          searchValue={inputValue}
                          onLoaded={({ items }: { items: Item[] }) => {
                            clearItems()
                            if (items) {
                              setHighlightedIndex(items.length ? 0 : null)
                              setItemCount(items.length)
                            }
                          }}
                          responseKey={dynamicConfig.responseKey}
                        >
                          {({ loading, items }: { loading: boolean, items: Item[] }) =>
                            <ItemList
                              loading={loading}
                              items={this.filterItems(items, selectedItems, dynamicConfig.omitItemsKey)}
                              getMenuProps={getMenuProps}
                              getItemProps={getItemProps}
                              highlightedIndex={highlightedIndex}
                            >
                              {(item: Item) => dynamicConfig.dynamicItemElement(item)}
                            </ItemList>}
                        </Fetch>
                      : null
}
                </div>
              </PillContainer>
            </div>
          )
        }}
      </Downshift>
    )
  }
}

export default Selector
